import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { X } from "lucide-react";

const KEY = "tr_cookie_consent_v1";

export default function CookieConsent() {
  const [open, setOpen] = useState(false);

  useEffect(() => {
    try {
      const stored = localStorage.getItem(KEY);
      if (!stored) setOpen(true);
    } catch {
      /* SSR / privacy mode — just leave it closed */
    }
  }, []);

  const save = (choice) => {
    try {
      localStorage.setItem(
        KEY,
        JSON.stringify({ choice, at: new Date().toISOString() })
      );
    } catch {
      /* ignore */
    }
    setOpen(false);
  };

  if (!open) return null;

  return (
    <div
      data-testid="cookie-consent"
      role="dialog"
      aria-live="polite"
      aria-label="Cookie consent"
      className="fixed inset-x-4 bottom-4 md:inset-x-auto md:right-6 md:bottom-6 md:max-w-[420px] z-[60] bg-ink text-white border border-white/10 shadow-2xl"
    >
      <div className="p-6 md:p-7">
        <div className="flex items-start justify-between gap-4 mb-3">
          <div className="overline text-vermilion">Cookies & privacy</div>
          <button
            onClick={() => save("dismissed")}
            data-testid="cookie-dismiss"
            aria-label="Dismiss"
            className="text-white/50 hover:text-white"
          >
            <X className="w-4 h-4" />
          </button>
        </div>
        <p className="text-sm text-white/80 leading-relaxed">
          We use essential browser storage only — a single entry to remember
          this choice, and (for admins) a sign-in token. No third-party
          analytics or marketing trackers run until you opt in.
        </p>
        <p className="text-xs text-white/50 mt-3 leading-relaxed">
          Read our{" "}
          <Link
            to="/privacy"
            className="underline underline-offset-2 hover:text-white"
            data-testid="cookie-privacy-link"
          >
            Privacy Policy
          </Link>{" "}
          for detail on retention, transfers and your GDPR/CCPA rights.
        </p>
        <div className="mt-5 flex flex-wrap gap-3">
          <button
            onClick={() => save("accepted")}
            data-testid="cookie-accept"
            className="inline-flex items-center justify-center bg-white text-ink hover:bg-vermilion hover:text-white px-5 py-3 text-[11px] uppercase tracking-[0.22em] font-semibold transition-colors"
          >
            Accept essential
          </button>
          <button
            onClick={() => save("rejected")}
            data-testid="cookie-reject"
            className="inline-flex items-center justify-center border border-white/30 text-white hover:bg-white/5 px-5 py-3 text-[11px] uppercase tracking-[0.22em] font-semibold transition-colors"
          >
            Reject non-essential
          </button>
        </div>
      </div>
    </div>
  );
}
